import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'

import { IPoolToken } from '../PairsTable'
import { getTransactionType } from '../TxnList'
import { formattedNum, formatTime } from '../../utils'
import { ExplorerDataType, getExplorerLink } from '../../utils/etherscan'
import { usedChain } from '../../constants'

// Styled Components based on Figma design
const TransactionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  width: 100%;

  @media screen and (max-width: 768px) {
    gap: 16px;
  }
`

const TransactionsTitle = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: white;
  line-height: 1;

  @media screen and (max-width: 768px) {
    font-size: 20px;
  }
`

const TransactionFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
  overflow-x: auto;

  @media screen and (max-width: 768px) {
    gap: 4px;
    padding-bottom: 4px;
  }
`

const FilterButton = styled.div<{ active: boolean }>`
  backdrop-filter: blur(16px);
  background: ${({ active }) => (active ? '#222328' : 'rgba(179, 179, 190, 0)')};
  border-radius: 32px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
    border-radius: 20px;
  }
`

const TransactionTable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  overflow-x: auto;
`

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;
  gap: 16px;
  width: 100%;
  min-width: 800px;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  opacity: 0.5;
  line-height: 1.4;

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const TransactionRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 20px 0;
  gap: 16px;
  width: 100%;
  min-width: 800px;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: white;
  line-height: 1.4;
  border-top: 1px solid #222328;
  border-bottom: 1px solid #222328;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.03);
  }

  &:first-of-type {
    border-top: 1px solid #222328;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    gap: 12px;
    min-width: unset;
    background: #1a1b20;
    border-radius: 12px;
    border: 1px solid #222328;
    margin-bottom: 12px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }

    &:first-of-type {
      border-top: 1px solid #222328;
    }
  }
`

const MobileTransactionCard = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`

const MobileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const MobileLabel = styled.span`
  font-size: 14px;
  opacity: 0.5;
  font-weight: 500;
`

const MobileValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  text-align: right;
`

const TransactionType = styled.div`
  width: 320px;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const TransactionValue = styled.div`
  flex: 1;
  min-width: 0;

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const TransactionAccount = styled.div`
  flex: 1;
  min-width: 0;

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const TransactionTime = styled.div`
  flex: 1;
  min-width: 0;
  text-align: right;

  @media screen and (max-width: 768px) {
    display: none;
  }
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: 768px) {
    gap: 8px;
    flex-direction: column;
    align-items: center;
  }
`

const PaginationInfo = styled.p`
  font-family: 'Inter Display', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 8px;
  }
`

const PaginationControls = styled.div`
  display: flex;
  gap: 4px;

  @media screen and (max-width: 768px) {
    gap: 4px;
  }
`

const PaginationArrow = styled.div<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};

  &:hover:not([disabled]) {
    background: rgba(255, 255, 255, 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media screen and (max-width: 768px) {
    width: 20px;
    height: 20px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  width: 100%;
  text-align: center;

  @media screen and (max-width: 768px) {
    padding: 60px 16px;
  }
`

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  @media screen and (max-width: 768px) {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
  }
`

const EmptyStateTitle = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: white;
  line-height: 1.4;
  margin-bottom: 8px;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
`

const EmptyStateDescription = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  max-width: 320px;

  @media screen and (max-width: 768px) {
    font-size: 13px;
    max-width: 280px;
  }
`

interface IPairTransaction {
  __typename: string
  amount0?: string
  amount1?: string
  amount0In?: string
  amount0Out?: string
  amount1In?: string
  amount1Out?: string
  amountUSD: string
  pair: {
    token0: IPoolToken
    token1: IPoolToken
  }
  sender?: string
  to?: string
  transaction: {
    id: string
    timestamp: string
  }
}

interface TableProps {
  items: IPairTransaction[]
}

const TXN_TYPE = {
  ALL: 'All',
  SWAP: 'Swap',
  ADD: 'Mint',
  REMOVE: 'Burn',
}

// Map GraphQL types to display types for getTransactionType function
const TYPE_MAPPING = {
  Mint: 'Add',
  Burn: 'Remove',
  Swap: 'Swap',
}

const filters = [
  { id: 'All', value: TXN_TYPE.ALL, displayName: 'All' },
  { id: 'swaps', value: TXN_TYPE.SWAP, displayName: 'Swaps' },
  { id: 'adds', value: TXN_TYPE.ADD, displayName: 'Adds' },
  { id: 'removes', value: TXN_TYPE.REMOVE, displayName: 'Removes' },
]

export const PairTransactionsTable: FC<TableProps> = ({ items }) => {
  const [page, setPage] = useState(1)
  const [filteredItems, setFilteredItems] = useState(items)
  const pageLimit = 10
  const [totalPages, setTotalPages] = useState(Math.ceil(items.length / pageLimit))
  const [txFilter, setTxFilter] = useState(TXN_TYPE.ALL)

  const handleFilterChange = (newTxFilter: string) => {
    if (txFilter === newTxFilter) return
    const newFilteredItems =
      newTxFilter === TXN_TYPE.ALL ? items : items.filter(({ __typename }) => __typename === newTxFilter)

    setPage(1)
    setFilteredItems(newFilteredItems)
    setTotalPages(Math.ceil(newFilteredItems.length / pageLimit))
    setTxFilter(newTxFilter)
  }

  const onPageChange = (direction: string) => {
    if (direction === 'next' && page < totalPages) {
      setPage(page + 1)
    } else if (direction === 'prev' && page > 1) {
      setPage(page - 1)
    }
  }

  useEffect(() => {
    setFilteredItems(items)
    setTotalPages(Math.ceil(items.length / pageLimit))
  }, [items])

  const displayedTransactions = filteredItems.slice((page - 1) * pageLimit, page * pageLimit)
  const startItem = (page - 1) * pageLimit + 1
  const endItem = Math.min(page * pageLimit, filteredItems.length)
  const totalItems = filteredItems.length

  return (
    <TransactionsContainer>
      <TransactionsTitle>Transactions</TransactionsTitle>

      <TransactionFilters>
        {filters.map(({ value, displayName }) => (
          <FilterButton key={value} active={txFilter === value} onClick={() => handleFilterChange(value)}>
            {displayName}
          </FilterButton>
        ))}
      </TransactionFilters>

      <TransactionTable>
        {displayedTransactions.length > 0 && (
          <TableHeader>
            <TransactionType></TransactionType>
            <TransactionValue>Total value</TransactionValue>
            <TransactionValue>Token amount</TransactionValue>
            <TransactionValue>Token amount</TransactionValue>
            <TransactionAccount>Account</TransactionAccount>
            <TransactionTime>Time</TransactionTime>
          </TableHeader>
        )}

        {displayedTransactions.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </EmptyStateIcon>
            <EmptyStateTitle>No transactions found</EmptyStateTitle>
            <EmptyStateDescription>
              {txFilter === TXN_TYPE.ALL
                ? 'There are no transactions available for this pair yet.'
                : `No ${txFilter.toLowerCase()} transactions found. Try selecting a different filter.`}
            </EmptyStateDescription>
          </EmptyState>
        ) : (
          displayedTransactions.map(
            ({
              __typename,
              amount0,
              amount1,
              amount0In,
              amount0Out,
              amount1In,
              amount1Out,
              amountUSD,
              pair,
              transaction,
              to,
              sender,
            }) => {
              const netToken0 = Number(amount0Out) - Number(amount0In)
              const netToken1 = Number(amount1Out) - Number(amount1In)

              let token0Symbol = pair.token0.symbol
              let token1Symbol = pair.token1.symbol
              let token0Amount = amount0
              let token1Amount = amount1

              if (__typename === TXN_TYPE.SWAP) {
                if (netToken0 < 0) {
                  token0Symbol = pair.token0.symbol
                  token1Symbol = pair.token1.symbol
                  token0Amount = Math.abs(netToken0).toString()
                  token1Amount = Math.abs(netToken1).toString()
                } else if (netToken1 < 0) {
                  token0Symbol = pair.token1.symbol
                  token1Symbol = pair.token0.symbol
                  token0Amount = Math.abs(netToken1).toString()
                  token1Amount = Math.abs(netToken0).toString()
                }
              }

              const handleRowClick = () => {
                const explorerLink = getExplorerLink(usedChain, transaction.id, ExplorerDataType.TRANSACTION)
                window.open(explorerLink, '_blank', 'noopener,noreferrer')
              }

              return (
                <TransactionRow key={`pair-transaction-table-${transaction.id}`} onClick={handleRowClick}>
                  {/* Desktop Layout */}
                  <TransactionType>
                    {getTransactionType(TYPE_MAPPING[__typename] || __typename, token0Symbol, token1Symbol)}
                  </TransactionType>
                  <TransactionValue>{formattedNum(amountUSD, true)}</TransactionValue>
                  <TransactionValue>{`${formattedNum(token0Amount)} ${token0Symbol}`}</TransactionValue>
                  <TransactionValue>{`${formattedNum(token1Amount)} ${token1Symbol}`}</TransactionValue>
                  <TransactionAccount>
                    {sender && sender.slice(0, 6) + '...' + sender.slice(38, 42)}
                    {to && to.slice(0, 6) + '...' + to.slice(38, 42)}
                  </TransactionAccount>
                  <TransactionTime>{formatTime(+transaction.timestamp)}</TransactionTime>

                  {/* Mobile Layout */}
                  <MobileTransactionCard>
                    <MobileRow>
                      <MobileLabel>Transaction</MobileLabel>
                      <MobileValue>
                        {getTransactionType(TYPE_MAPPING[__typename] || __typename, token0Symbol, token1Symbol)}
                      </MobileValue>
                    </MobileRow>
                    <MobileRow>
                      <MobileLabel>Total Value</MobileLabel>
                      <MobileValue>{formattedNum(amountUSD, true)}</MobileValue>
                    </MobileRow>
                    <MobileRow>
                      <MobileLabel>Token Amounts</MobileLabel>
                      <MobileValue>
                        {`${formattedNum(token0Amount)} ${token0Symbol}`}
                        <br />
                        {`${formattedNum(token1Amount)} ${token1Symbol}`}
                      </MobileValue>
                    </MobileRow>
                    <MobileRow>
                      <MobileLabel>Account</MobileLabel>
                      <MobileValue>
                        {sender && sender.slice(0, 6) + '...' + sender.slice(38, 42)}
                        {to && to.slice(0, 6) + '...' + to.slice(38, 42)}
                      </MobileValue>
                    </MobileRow>
                    <MobileRow>
                      <MobileLabel>Time</MobileLabel>
                      <MobileValue>{formatTime(+transaction.timestamp)}</MobileValue>
                    </MobileRow>
                  </MobileTransactionCard>
                </TransactionRow>
              )
            }
          )
        )}
      </TransactionTable>

      {totalItems > 0 && (
        <PaginationContainer>
          <PaginationInfo>{`${startItem}-${endItem} of ${totalItems}`}</PaginationInfo>

          <PaginationControls>
            <PaginationArrow disabled={page <= 1} onClick={() => onPageChange('prev')}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 4L6 8L10 12"
                  stroke="rgba(255, 255, 255, 0.6)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PaginationArrow>

            <PaginationArrow disabled={page >= totalPages} onClick={() => onPageChange('next')}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 4L10 8L6 12"
                  stroke="rgba(255, 255, 255, 0.6)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PaginationArrow>
          </PaginationControls>
        </PaginationContainer>
      )}
    </TransactionsContainer>
  )
}
